/**
 * The run's progress as STEPS a reader can follow (#1400) — shared by the Hub's run follower
 * (`ReunderwriteRun`) and the analyzer's running page (`AnalysisView`).
 *
 * ⚠️ IT DRAWS ONLY WHAT THE SERVER REPORTED. Every step before the reported one is `done` because the
 * worker writes the stages in order and only at the checkpoints it passes; the reported one is
 * `current`; the rest are `upcoming`. There is no percentage and no time estimate — the stages are
 * nowhere near equal in length (the underwrite dwarfs the proforma read), so a bar drawn from them
 * would be a guess drawn as a measurement.
 */

import { ANALYSIS_PROGRESS_STAGES, type AnalysisProgressStage } from './api.js';

/** What each step is called on screen. Short — they sit in a narrow notice on a phone. */
export const STAGE_LABEL: Record<AnalysisProgressStage, string> = {
  documents: 'Bringing the documents in',
  extraction: 'Reading the documents',
  proforma: 'Reading the proforma workbook',
  // One step, not two: the public-record checks run CONCURRENTLY with the scoring, so ordering them
  // would claim a sequence the worker does not follow.
  underwrite: 'Scoring the deal and checking the public record',
  findings: 'Checking the findings',
};

export type StepState = 'done' | 'current' | 'upcoming';

export interface ProgressStep {
  stage: AnalysisProgressStage;
  label: string;
  state: StepState;
}

function isKnownStage(stage: string): stage is AnalysisProgressStage {
  return (ANALYSIS_PROGRESS_STAGES as readonly string[]).includes(stage);
}

/**
 * The ordered steps for a reported stage, or `null` when there is nothing honest to draw: no stage
 * reported, or one this build does not know (a server newer than the page). `null` sends the caller back
 * to its status + elapsed copy — never to a list with a step it cannot place.
 */
export function progressSteps(stage: string | null | undefined): ProgressStep[] | null {
  if (!stage || !isKnownStage(stage)) return null;
  const at = ANALYSIS_PROGRESS_STAGES.indexOf(stage);
  return ANALYSIS_PROGRESS_STAGES.map((s, i) => ({
    stage: s,
    label: STAGE_LABEL[s],
    state: i < at ? 'done' : i === at ? 'current' : 'upcoming',
  }));
}
